import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  HelpCircle, Mail, FileText, Eye, Pencil, ArrowLeftRight, Pause, PauseCircle,
  Trash2, Video, CalendarClock, CreditCard, AlertTriangle,
} from "lucide-react";

/** "¿Qué hace cada botón?" de la fila de un estudiante. Lo pidió la dueña para no
    tener que buscar la explicación en el chat cada vez. */
const ACCIONES = [
  { Icono: Video, color: "text-emerald-600", nombre: "Meet / Zoom",
    que: "Abre el link de clase del grupo en una pestaña nueva. Solo aparece si el grupo tiene link configurado." },
  { Icono: Mail, color: "text-brand-blue", nombre: "Reenviar correo de matrícula",
    que: "Le manda otra vez al estudiante el correo de bienvenida con sus datos del programa." },
  { Icono: FileText, color: "text-emerald-700", nombre: "Descargar factura",
    que: "Baja el PDF de la factura de esa matrícula, en el idioma que tengas puesto." },
  { Icono: Eye, color: "text-slate-700", nombre: "Ver asistencia",
    que: "Abre la ventana de asistencia del estudiante: ciclos, fechas y el contador de clases (8/8)." },
  { Icono: Pencil, color: "text-brand-blue", nombre: "Editar matrícula",
    que: "El formulario completo: programa, precio, clases por ciclo, día de arranque, facturación compartida, autoreserva." },
  { Icono: ArrowLeftRight, color: "text-violet-700", nombre: "Cambiar de plan",
    que: "Pasa al estudiante a otro plan (más o menos clases por ciclo, otro precio). Arranca en el ciclo siguiente: nunca parte el ciclo en curso." },
  { Icono: Pause, color: "text-amber-600", nombre: "Suspender el cobro",
    que: "Frena SOLO la facturación: no se emite la factura del ciclo siguiente. Las clases siguen normal. Se le puede poner plazo en meses y se reactiva sola.",
    activo: "Cuando está activa, el botón se pone ámbar sólido y al lado del nombre sale la etiqueta SUSPENDIDO." },
  { Icono: PauseCircle, color: "text-amber-700", nombre: "Pausa por viaje",
    que: "Frena ASISTENCIA Y COBRO, con fecha de salida y de regreso. Las clases de ese rango no cuentan y el ciclo no cierra hasta la primera medianoche después del regreso.",
    activo: "Cuando está activa, el botón se pone rojo sólido. Solo la ve el superadmin." },
  { emoji: "📦", nombre: "Archivar programa",
    que: "Manda el programa al historial del estudiante. Se conserva todo y se reactiva cuando vuelve. Viene marcada la casilla de frenar también la facturación.",
    activo: "Viendo archivados, este botón cambia a ↩️ para reactivar." },
  { Icono: Trash2, color: "text-red-600", nombre: "Borrar programa",
    que: "Elimina el programa y sus registros de asistencia. Los pagos se conservan por contabilidad, salvo que marques la casilla.",
    activo: "Si querés conservar el historial, archivá en vez de borrar." },
];

export const MemberActionsHelp = () => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="member-actions-help-btn"
                title="¿Qué hace cada botón?">
          <HelpCircle className="h-4 w-4" />
          <span className="hidden sm:inline">¿Qué hace cada botón?</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="member-actions-help-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-brand-blue" />
            Los botones de la fila de un estudiante
          </DialogTitle>
          <DialogDescription>
            De izquierda a derecha, tal como aparecen al lado del nombre.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2.5">
            <div className="flex gap-2.5">
              <span className="text-[10px] font-bold text-emerald-700 bg-white border border-slate-200 rounded px-1.5 py-0.5 h-fit whitespace-nowrap">
                4/4 · 100%
              </span>
              <p className="text-[12.5px] text-slate-600 leading-snug">
                Clases consumidas del ciclo en curso. Verde desde 100 %, naranja desde 75 %, gris
                por debajo. Pasando el mouse por la barra ves cuántas ausencias justificadas lleva
                usadas del cupo.
              </p>
            </div>
            <div className="flex gap-2.5">
              <span className="flex items-center gap-1 text-slate-500 h-fit pt-0.5">
                <CalendarClock className="h-3.5 w-3.5" />
                <CreditCard className="h-3.5 w-3.5" />
              </span>
              <p className="text-[12.5px] text-slate-600 leading-snug">
                <b>Fecha de inicio</b> también es un botón: abre la edición rápida de fecha de
                inicio, link de pago y cuenta bancaria, con la opción de guardar o guardar y
                reenviar el correo. La tarjetita aparece solo si esa matrícula tiene link de pago.
              </p>
            </div>
          </div>

          {ACCIONES.map(({ Icono, emoji, color, nombre, que, activo }) => (
            <div key={nombre} className="flex gap-3 border-b border-slate-100 pb-3 last:border-0">
              <span className="h-8 w-8 shrink-0 rounded-lg bg-slate-50 border border-slate-200 grid place-items-center">
                {emoji ? <span className="text-sm">{emoji}</span> : <Icono className={`h-4 w-4 ${color}`} />}
              </span>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-slate-900">{nombre}</div>
                <p className="text-[12.5px] text-slate-600 leading-snug mt-0.5">{que}</p>
                {activo && <p className="text-[11.5px] text-slate-400 leading-snug mt-1">{activo}</p>}
              </div>
            </div>
          ))}

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-900">
              <AlertTriangle className="h-3.5 w-3.5" /> Las dos pausas se confunden fácil
            </div>
            <ul className="mt-1.5 space-y-1 text-[12.5px] text-amber-900/90">
              <li className="flex gap-2">
                <Pause className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                <span>La <b>pausa ámbar</b> frena solo la plata. Las clases siguen.</span>
              </li>
              <li className="flex gap-2">
                <PauseCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" />
                <span>
                  La <b>pausa en círculo</b> frena plata <b>y</b> asistencia, y además congela el
                  cierre del ciclo hasta la primera medianoche después del regreso.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
